"use client";

import * as React from "react";
import { ArrowUp, ArrowDown, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const testimonials = [{
  id: 1,
  quote: "The team at Caravan Smash Repairs did an incredible job fixing the hail damage on my Jayco. It looks brand new again, and the insurance process was completely seamless.",
  author: "Mark Thompson",
  role: "Jayco Journey Owner",
  hotel: "South Windsor, NSW",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/logo_noverian_white.png"
}, {
  id: 2,
  quote: "Expert structural repairs and electrical work. They installed a new solar system and battery setup that has completely changed our off-grid camping experience.",
  author: "Sarah Jenkins",
  role: "Off-Road Specialist",
  hotel: "Penrith Area",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/logo_marilena_white.png"
}, {
  id: 3,
  quote: "Professional, efficient, and high-quality craftsmanship. They are the only shop I trust in Sydney for my caravan's plumbing and chassis maintenance. Highly recommended.",
  author: "David Wilson",
  role: "Fleet Manager",
  hotel: "Sydney Rentals",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/logo_royal_marmin_white.png"
}, {
  id: 4,
  quote: "After a minor accident, I was worried about the structural integrity. The guys here were thorough and provided a detailed report. Exceptional service and peace of mind!",
  author: "Linda Carmichael",
  role: "Motorhome Enthusiast",
  hotel: "Blue Mountains",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1599594/img/logo_creteco_white.png"
}];
export default function ClientSuccessStory() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return <section data-section-id="25102" id="customer-testimonials" ref={ref} className={cn("bg-background relative flex min-h-[800px] w-full flex-col items-center justify-center overflow-hidden py-24 transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-0 h-full w-full overflow-hidden pointer-events-none">
        <div className="bg-secondary/5 absolute -top-[20%] -left-[10%] h-[600px] w-[600px] rounded-full blur-[120px]" />
        <div className="bg-accent/5 absolute -bottom-[20%] -right-[10%] h-[600px] w-[600px] rounded-full blur-[120px]" />
      </div>

      <div className="container relative z-10 mx-auto px-6 md:px-12">
        <div className="mb-16 text-center">
          <h2 className="text-accent mb-4 text-sm font-mono font-bold tracking-widest uppercase">
            Customer Feedback
          </h2>
          <h3 className="text-primary font-sans text-4xl font-extrabold tracking-tight md:text-5xl">
            Trusted by Caravan Owners
          </h3>
        </div>

        <div className="flex justify-center">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} orientation="vertical" className="w-full max-w-4xl">
            <CarouselContent className="-mt-1 h-[500px]">
              {testimonials.map((item, i) => <CarouselItem key={item.id} className="pt-1 md:basis-1/2" data-index={i}>
                  <div className="p-4">
                    <Card className="border-l-4 border-accent border-y-border border-r-border bg-card shadow-xl backdrop-blur-md transition-all duration-500 hover:shadow-2xl">
                      <CardContent className="flex flex-col items-center justify-center p-10 text-center">
                        <Quote className="text-secondary mb-6 h-10 w-10 opacity-90" />
                        
                        <blockquote className="mb-8 max-w-2xl">
                          <p className="text-primary/90 font-sans text-xl italic leading-relaxed md:text-2xl">
                            "{item.quote}"
                          </p>
                        </blockquote>

                        <div className="flex flex-col items-center gap-4">
                          <div className="h-px w-12 bg-border" />
                          <div>
                            <h4 className="text-primary text-lg font-bold font-sans">
                              {item.author}
                            </h4>
                            <p className="text-muted-foreground font-mono text-xs uppercase tracking-wider">
                              {item.role} • {item.hotel}
                            </p>
                          </div>
                          
                          {}
                          <div className="mt-4 opacity-40 transition-opacity hover:opacity-100">
                             {}
                             <img src={item.logo} alt={`${item.hotel} logo`} className="h-8 w-auto grayscale brightness-50" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="absolute -right-12 top-1/2 flex -translate-y-1/2 flex-col gap-4 md:-right-24">
              <CarouselPrevious variant="ghost" className="text-secondary hover:text-secondary/80 hover:bg-muted border-secondary/30 h-12 w-12 rounded-sm border transition-all duration-300 hover:scale-110 [&_svg]:h-6 [&_svg]:w-6">
                <ArrowUp className="h-6 w-6" />
                <span className="sr-only">Previous</span>
              </CarouselPrevious>
              
              <CarouselNext variant="ghost" className="text-secondary hover:text-secondary/80 hover:bg-muted border-secondary/30 h-12 w-12 rounded-sm border transition-all duration-300 hover:scale-110 [&_svg]:h-6 [&_svg]:w-6">
                <ArrowDown className="h-6 w-6" />
                <span className="sr-only">Next</span>
              </CarouselNext>
            </div>
          </Carousel>
        </div>

        {}
        <div className="mt-12 flex justify-center gap-2">
          {Array.from({
          length: count
        }).map((_, index) => <button key={index} data-index={index} onClick={() => api?.scrollTo(index)} className={cn("h-1.5 rounded-full transition-all duration-300", current === index + 1 ? "bg-accent w-8" : "bg-muted w-2 hover:bg-secondary/40")} aria-label={`Go to slide ${index + 1}`} />)}
        </div>
      </div>
    </section>;
}